import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import MainLayout from '@sophocles/app/main_layout';
import { useScope } from '@sophocles/app/ScopeContext';
import { UserProvider, useUser } from '@sophocles/app/UserContext';
import NetworkStatusBanner from '@sophocles/components/App/NetworkStatusBanner';
import ToastContent from '@sophocles/components/ToastContent';
import { DataGridConfigProvider } from '@sophocles/data-grids';
import { fetchAuthUser } from '@sophocles/features/auth/authActions';
import { odlDataGridConfig } from '@sophocles/features/entities/dataGridConfig';
import { useKeyboardAvoidance } from '@sophocles/platform';
import tokenService from '@sophocles/services/token';
import { ThemeSchemeProvider, TenantThemeProvider } from '@sophocles/theme';

import ApiHealthGate from './ApiHealthGate';

window.history.scrollRestoration = 'manual';

// Legacy: old ThemeContext / setSwatches API.  The new theme system
// (ThemeSchemeProvider + TenantThemeProvider) doesn't need it; we
// keep a no-op shim so existing consumers (e.g. components/App/App.js)
// don't break in a single MR.  Remove once those callers are migrated.
export const ThemeContext = React.createContext({ setSwatches: () => {} });

const Root = () => {
  const dispatch = useDispatch();
  const wasInactiveRef = useRef(false);

  // Soft-keyboard avoidance for forms / modals on native — no-op on web.
  useKeyboardAvoidance();

  useEffect(() => {
    App.addListener('appUrlOpen', data => {
      const url = data.url.replace('odl:/', '');
      try {
        window.location.href = url;
        Browser.close();
      } catch (error) {
        console.error('Navigation error:', error);
      }
    });
  }, []);

  // On Capacitor native, revalidate /users/me/ once when the app
  // returns from the background. Without this, route guards on the
  // first navigation after resume run against a potentially stale
  // rehydrated user record (e.g. is_staff missing, group memberships
  // not refreshed) and incorrectly fall through to the "couldn't
  // load" UI. RequireAuth's per-attempt refetch eventually corrects
  // this on retry, but doing it once on resume avoids the user ever
  // seeing the failure UI in the first place.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let handle;
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        wasInactiveRef.current = true;
        return;
      }
      if (!wasInactiveRef.current) return;
      wasInactiveRef.current = false;
      if (tokenService.idToken) {
        dispatch(fetchAuthUser());
      }
    }).then(h => {
      handle = h;
    });
    return () => {
      if (handle) handle.remove();
    };
  }, [dispatch]);

  // Native (Capacitor) bottom-center; web top-right. Bottom feels
  // right on mobile because the user's thumbs are there; top-right
  // is the desktop convention.
  const isNativePlatform = Capacitor.isNativePlatform();
  const toastAnchor = useMemo(
    () => (isNativePlatform ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' }),
    [isNativePlatform],
  );
  // Same custom card-style content wired into every variant so the
  // look stays consistent across success / error / warning / info /
  // default. The component reads ``variant`` to pick its accent
  // colour and icon.
  const toastComponents = useMemo(
    () => ({
      default: ToastContent,
      success: ToastContent,
      error: ToastContent,
      warning: ToastContent,
      info: ToastContent,
    }),
    [],
  );
  const snackbarStyle = isNativePlatform
    ? {
        marginBottom: 'env(safe-area-inset-bottom)',
        marginTop: 'env(safe-area-inset-top)',
      }
    : undefined;

  return (
    <BrowserRouter>
      <ThemeContext.Provider value={{ setSwatches: () => {} }}>
        <ThemeSchemeProvider>
          <TenantThemeProvider>
            <CssBaseline />
            <NetworkStatusBanner />
            {/* SnackbarProvider sits inside ThemeSchemeProvider so
                toast components inherit the active MUI theme (light
                vs dark). When this lived in src/index.js outside the
                theme, notistack portals rendered with MUI's default
                light theme — dark text on dark surface in dark
                mode. */}
            <SnackbarProvider
              Components={toastComponents}
              maxSnack={3}
              anchorOrigin={toastAnchor}
              style={snackbarStyle}
            >
              <UserProvider>
                {/* DataGridConfigProvider injects ODL-specific dependencies
                    (apiClient, spec query, reference resolution, param
                    serializer, i18n, formatters) into the grid machinery.
                    Mounted inside UserProvider so hooks that need auth
                    (useUserPreference) are available when exercised. */}
                <DataGridConfigProvider config={odlDataGridConfig}>
                  <ApiHealthGate>
                    <MainLayout />
                  </ApiHealthGate>
                </DataGridConfigProvider>
              </UserProvider>
            </SnackbarProvider>
          </TenantThemeProvider>
        </ThemeSchemeProvider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
};

export { useScope, useUser };
export default Root;
